/**
 * Build share URLs with UTM tracking for each platform.
 *
 * - Direct / SMS / copy-link: uses clean branded URL (say-it-now.app/a/{slug})
 * - Social platforms: uses OG edge-function URL so crawlers get meta tags
 */

import { getShareText } from './shareText';
import { getAnnouncementShareUrl, getOgShareUrl } from './shareUrl';

interface BuildShareLinksParams {
  publicSlug: string;
  templateSlug?: string;
  relationship?: string;
  userRef?: string;
  senderName?: string | null;
}

interface ShareLinks {
  canonical: string;
  facebook: string;
  x: string;
  linkedin: string;
  whatsapp: string;
}

function appendUtm(base: string, platform: string, templateSlug?: string, userRef?: string): string {
  const url = new URL(base);
  url.searchParams.set('utm_source', platform);
  url.searchParams.set('utm_medium', 'share');
  url.searchParams.set('utm_campaign', 'living_announcement');
  url.searchParams.set('utm_content', templateSlug || 'unknown');
  if (userRef) url.searchParams.set('ref', userRef);
  return url.toString();
}

export function buildShareLinks({
  publicSlug,
  templateSlug,
  relationship,
  userRef,
  senderName,
}: BuildShareLinksParams): ShareLinks {
  const cleanUrl = getAnnouncementShareUrl(publicSlug);
  const ogUrl = getOgShareUrl(publicSlug);

  // Canonical / direct link — clean branded URL
  const canonical = appendUtm(cleanUrl, 'direct', templateSlug, userRef);

  // Social platforms — use OG URL so crawlers get meta tags (users never see this URL)
  const fbUrl = appendUtm(ogUrl, 'facebook', templateSlug, userRef);
  const xUrl = appendUtm(ogUrl, 'x', templateSlug, userRef);
  const liUrl = appendUtm(ogUrl, 'linkedin', templateSlug, userRef);
  // WhatsApp — clean URL since it shows the link text to the recipient
  const waUrl = appendUtm(cleanUrl, 'whatsapp', templateSlug, userRef);

  const shareText = encodeURIComponent(getShareText(senderName, relationship));

  return {
    canonical,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fbUrl)}`,
    x: `https://x.com/intent/tweet?url=${encodeURIComponent(xUrl)}&text=${shareText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(liUrl)}`,
    whatsapp: `https://wa.me/?text=${shareText}%20${encodeURIComponent(waUrl)}`,
  };
}
